// src/pages/auth/Login.js
import React, { useState } from 'react';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import { Box, Typography, TextField, Button, Checkbox, FormControlLabel, Link, Paper, Divider, SvgIcon, InputAdornment, IconButton } from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import GoogleAuthButton from './GoogleAuthButton';
import { Visibility, VisibilityOff } from '@mui/icons-material';
// Google colored icon component

function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [errors, setErrors] = useState({
    email: '',
    password: '',
    general: '',
  });
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Debug: Monitor errors state changes
  React.useEffect(() => {
    console.log('Errors state changed:', errors);
  }, [errors]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: '',
      });
    }
  };

  const validateForm = () => {
    const newErrors = {
      email: '',
      password: '',
      general: '',
    };
    
    // Validate email
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    
    // Validate password
    if (!formData.password) {
      newErrors.password = 'Password is required';
    }
    
    setErrors(newErrors);
    
    // Return true if no errors
    return !newErrors.email && !newErrors.password;
  };

  const handleSubmit = async (e) => {
    console.log('handleSubmit called');
    e.preventDefault();
    
    console.log('Form submitted with data:', formData);
    
    if (!validateForm()) {
      console.log('Form validation failed');
      return;
    }
    
    setLoading(true);
    
    try {
      const result = await login({
        ...formData,
      });
      console.log('Login result:', result);
      if (result.success) {
        const from = location.state?.from;
        const redirectUrl = from ? from.pathname : result.redirect_url || '/dashboard';
        
        navigate(redirectUrl);
      } else {
        console.log('Login failed, setting error:', result.error);
        setErrors(prev => ({
          ...prev,
          general: result.error || 'Invalid email or password',
        }));
      }
    } catch (error) {
      console.error('Login catch error:', error);
      setErrors(prev => ({
        ...prev,
        general: error.message || 'Failed to login. Please try again.',
      }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper
      elevation={2}
      sx={{
        width: '100%',
        p: { xs: 3, sm: 3 },
        borderRadius: 2,
        backgroundColor: 'white',
      }}
    >
      <Typography variant="h5" component="h1" align="center" fontWeight="medium" gutterBottom>
        Welcome!
      </Typography>
      <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 2 }}>
        Log in to Access Your Admin Account.
      </Typography>

      {errors.general && (
        <Box 
          sx={{ 
            backgroundColor: '#FFECEC', 
            color: '#D32F2F', 
            p: 1.5, 
            borderRadius: 1, 
            mb: 2,
            fontSize: '0.875rem',
          }}
        >
          {errors.general}
        </Box>
      )}

      <form onSubmit={handleSubmit} style={{ width: '100%' }}>
        <TextField
          margin="dense"
          required
          fullWidth
          id="email"
          label="Email"
          name="email"
          autoComplete="email"
          autoFocus
          size="small"
          value={formData.email}
          onChange={handleChange}
          error={!!errors.email}
          helperText={errors.email}
          mb={2}
          sx={{
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: errors.email ? '#d32f2f' : undefined,
              },
              '&.Mui-error fieldset': {
                borderColor: '#d32f2f !important',
                borderWidth: '2px',
              },
            },
            '& .Mui-error .MuiOutlinedInput-notchedOutline': {
              backgroundColor: 'rgba(211, 47, 47, 0.04)',
              borderColor: '#d32f2f !important',
            },
          }}
        />
        
        <TextField
          margin="dense"
          required
          fullWidth
          name="password"
          label="Password"
          type={showPassword ? "text" : "password"}
          id="password"
          size="small"
          autoComplete="current-password"
          value={formData.password}
          onChange={handleChange}
          error={!!errors.password}
          helperText={errors.password}
          mt={2}
          sx={{
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: errors.password ? '#d32f2f' : undefined,
              },
              '&.Mui-error fieldset': {
                borderColor: '#d32f2f !important',
                borderWidth: '2px',
              },
            },
            '& .Mui-error .MuiOutlinedInput-notchedOutline': {
              backgroundColor: 'rgba(211, 47, 47, 0.04)',
              borderColor: '#d32f2f !important',
            },
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={() => setShowPassword((show) => !show)}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
          <FormControlLabel
            control={
              <Checkbox 
                checked={rememberMe} 
                onChange={(e) => setRememberMe(e.target.checked)} 
                color="primary" 
                size="small" 
              />
            }
            label={<Typography variant="body2">Remember</Typography>}
          />
          <Link component={RouterLink} to="/auth/forgot-password" variant="body2" color="primary">
            Forgot Password?
          </Link>
        </Box>
        
        <Button
          type="submit"
          fullWidth
          variant="contained"
          sx={{ 
            mt: 2, 
            mb: 2, 
            py: 1,
            background: 'linear-gradient(45deg, #007050 30%, #00a070 90%)',
            boxShadow: '0 3px 5px 2px rgba(0, 112, 80, .3)',
            '&:hover': {
              background: 'linear-gradient(45deg, #00513a 30%, #007050 90%)',
              boxShadow: '0 3px 5px 2px rgba(0, 81, 58, .3)',
            },
          }}
          disabled={loading}
        >
          {loading ? 'Logging in...' : 'Login'}
        </Button>
        
        <Box sx={{ mb: 2, mt: 2, display: 'flex', alignItems: 'center' }}>
          <Divider sx={{ flexGrow: 1 }} />
          <Typography variant="body2" color="text.secondary" sx={{ px: 2 }}>
            OR
          </Typography>
          <Divider sx={{ flexGrow: 1 }} />
        </Box>

        <GoogleAuthButton action="login" />
      </form>
    </Paper>
  );
}

export default Login;